import { useState } from "react";
import useODOCreate from "../../hooks/cs/useODOCreate";
import { Controller, useForm } from "react-hook-form";
import { toB64Obj, toDataUri } from "../../utils/fileUtils";
import { ODOCreateRequest } from "../../types/cs";
import { DebouncedInput } from "../DebouncedInput";
import { ImageIcon, Send, Upload, X } from "lucide-react";

interface ODOCreateFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormData {
  title: string;
  service_type: string;
  description: string;
}

interface ImageData {
  file: File;
  base64: string;
  preview: string;
}

const ODOCreateForm = ({ onSuccess, onCancel }: ODOCreateFormProps) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { createODO, isLoading, error, clearError } = useODOCreate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      title: "",
      service_type: "",
      description: "",
    },
  });

  const watchedDescription = watch("description", "");

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    clearError();

    try {
      const newImages: ImageData[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith("image/")) {
          console.warn(`${file.name}은(는) 이미지 파일이 아닙니다.`);
          continue;
        }

        try {
          const result = await toB64Obj(file);
          const preview = toDataUri(result);

          newImages.push({
            file,
            base64: result.data,
            preview,
          });
        } catch (error) {
          console.error(`이미지 변환 실패: ${file.name}`, error);
          alert(`${file.name} 변환에 실패했습니다.`);
        }
      }

      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
      }
    } catch (error) {
      console.error("이미지 업로드 오류:", error);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FormData) => {
    clearError();

    const requestData: ODOCreateRequest = {
      title: data.title.trim(),
      service_type: "home",
      description: data.description.trim(),
      reference_image:
        images.length > 0 ? images.map((img) => img.base64).join(",") : "",
    };

    const success = await createODO(requestData);

    if (success) {
      reset();
      onSuccess?.();
      setImages([]);
    }
  };

  const handleCancel = () => {
    reset();
    setImages([]);
    clearError();
    onCancel?.();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            제목 <span className="text-red-500">*</span>
          </label>
          <Controller
            name="title"
            control={control}
            rules={{
              required: "제목은 필수 입력 항목입니다.",
              minLength: {
                value: 1,
                message: "제목은 최소 1자 이상이어야 합니다.",
              },
              maxLength: {
                value: 100,
                message: "제목은 최대 100자 이하여야 합니다.",
              },
            }}
            render={({ field }) => (
              <DebouncedInput
                value={field.value}
                onChange={field.onChange}
                placeholder="제목을 입력해주세요."
                maxLength={100}
                delay={300}
                className="w-full"
              />
            )}
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
          )}
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-2">
            문의 내용 <span className="text-red-500">*</span>
          </label>
          <Controller
            name="description"
            control={control}
            rules={{
              required: "문의 내용은 필수 입력 항목입니다.",
              maxLength: {
                value: 1000,
                message: "문의 내용은 최대 1000자 이하여야 합니다.",
              },
            }}
            render={({ field }) => (
              <DebouncedInput
                value={field.value}
                onChange={field.onChange}
                placeholder="문의 내용을 입력해주세요."
                maxLength={1000}
                delay={300}
                className="w-full"
                type="textarea"
                rows={10}
              />
            )}
          />
          <div className="flex justify-between mt-1">
            {errors.description && (
              <p className="text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {watchedDescription.length}/1000
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            첨부 이미지 (선택사항)
          </label>
          <div className="space-y-4">
            <div className="flex items-center justify-center w-full">
              <label
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isUploading
                    ? "border-teal-300 bg-teal-50"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {isUploading ? (
                    <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
                  ) : (
                    <Upload className="w-8 h-8 mb-4 text-gray-500" />
                  )}
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">
                      {isUploading ? "업로드 중..." : "클릭하여 업로드"}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG 파일 (여러 개 선택 가능)
                  </p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            </div>

            {images.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ImageIcon className="w-4 h-4" />
                  <span>첨부된 이미지 ({images.length}개)</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.preview}
                        alt={img.file.name}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-xs px-2 py-1 rounded text-center truncate backdrop-blur-sm">
                        {img.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <X className="w-3 h-3 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">문의 등록 실패</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isLoading || isUploading}
            className="flex items-center gap-2 px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {isLoading ? "전송 중..." : "문의하기"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ODOCreateForm;
